import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  DOCUMENT,
  ErrorHandler,
  provideAppInitializer,
  inject,
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  provideRouter,
  Router,
  withNavigationErrorHandler,
  withViewTransitions,
} from '@angular/router';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { environment } from '@environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { defaultIfEmpty, firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor, timeoutInterceptor } from './core/interceptors';
import { initializeAuth } from './core/initializers/auth.initializer';
import { SentryErrorHandler } from './core/errors/sentry-error-handler';
import { isChunkLoadError, recoverFromChunkError } from './core/errors/chunk-load-error';
import {
  captureMessage,
  reportUnhandledError,
  startRouterTracing,
} from './core/services/sentry-client';

/**
 * True when any route in the activated chain opts into the cross-fade.
 *
 * The router hands `onViewTransitionCreated` the *root* snapshot
 * (`targetSnapshot.root`), whose `data` is always empty — the `crossfade` flag
 * lives on the child routes in `app.routes.ts`. Walking the chain also keeps
 * this correct wherever the flag sits and whatever `paramsInheritanceStrategy`
 * is in effect.
 */
function wantsCrossfade(route: ActivatedRouteSnapshot | null): boolean {
  for (let node = route; node; node = node.firstChild) {
    if (node.data['crossfade']) return true;
  }
  return false;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      // Subtle cross-fade between the public/auth routes only (landing ↔ login /
      // forgot-password / select-account), opted-in via `data.crossfade` on
      // those routes. The authenticated app keeps its instant navigations.
      // The Gozem logo carries a shared view-transition-name (styles.css) so it
      // persists/morphs across the boundary instead of blinking out and back.
      withViewTransitions({
        skipInitialTransition: true,
        // Deliberately NOT `transition.skipTransition()`: the router attaches
        // its own dev-only `.catch(console.error)` to `transition.ready`
        // *before* invoking this hook, so skipping always prints
        // "AbortError: Transition was skipped" and cannot be suppressed from
        // application code. Instead every transition is allowed to complete —
        // `ready` resolves, nothing is logged — and whether it *animates* is
        // gated in CSS off `.vt-crossfade` (styles.css).
        onViewTransitionCreated: ({ transition, to }) => {
          const root = inject(DOCUMENT).documentElement;
          if (!wantsCrossfade(to)) {
            root.classList.remove('vt-crossfade');
            return;
          }
          root.classList.add('vt-crossfade');
          // `finished` rejects if the update callback throws; the class has to
          // come off either way, and this chain must not itself become an
          // unhandled rejection.
          void transition.finished
            .catch(() => undefined)
            .finally(() => root.classList.remove('vt-crossfade'));
        },
      }),
      // Recover from stale lazy-route chunks after a deploy: when the old,
      // content-hashed chunk a pre-deploy tab expects has 404'd, the dynamic
      // import fails here as a NavigationError. We reload onto the intended URL
      // (fresh index.html + new chunk map) rather than dead-ending the user.
      // Guarded against loops + offline in recoverFromChunkError. Non-chunk
      // navigation errors keep their existing path to the ErrorHandler/Sentry.
      // See docs/chunk-load-recovery.md.
      withNavigationErrorHandler((navError) => {
        if (isChunkLoadError(navError.error)) {
          captureMessage('Recovered from stale lazy-chunk (navigation)', {
            level: 'warning',
            extra: { url: navError.url },
          });
          if (recoverFromChunkError(navError.url)) return;
        }
        reportUnhandledError(navError.error);
      }),
    ),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAnimationsAsync(),
    // `timeoutInterceptor` first, so it sits OUTSIDE `authInterceptor` and its
    // budget covers total wall-clock — including the 401 refresh-and-replay and
    // the queue behind an in-flight refresh, which can otherwise hang forever.
    provideHttpClient(withInterceptors([timeoutInterceptor, authInterceptor])),
    providePrimeNG({}),

    provideAppInitializer(() => {
      const restoreAuth = initializeAuth();
      return firstValueFrom(restoreAuth().pipe(defaultIfEmpty(null))).catch(() => null);
    }),
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
    // Router performance tracing attaches once the deferred SDK resolves; not
    // awaited, so it never delays bootstrap.
    provideAppInitializer(() => {
      startRouterTracing(inject(Router));
    }),
  ],
};
